import { Ban, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PIPELINE_CFG = { endpoint: "/agent-stats/groups", field: "pipelines", noun: "pipelines", title: "Créer un groupe de pipelines", placeholder: "ex. Prospection", prefix: "pipeline-group" };
const TAG_CFG = { endpoint: "/agent-stats/tag-groups", field: "tags", noun: "tags", title: "Créer un groupe de tags", placeholder: "ex. Sources web", prefix: "tag-group" };
const AGENT_CFG = { endpoint: "/agent-stats/agent-groups", field: "agents", noun: "agents", title: "Créer un groupe d'agents", placeholder: "ex. Comptes installateurs", prefix: "agent-group" };

const stepsOf = (allSteps, p) => allSteps.filter((o) => o.pipeline === p).map((o) => o.id);

export const expandGroupSteps = (g, allSteps = []) => {
  if (!g.steps?.length) return [];
  return g.pipelines.flatMap((p) => {
    const ids = stepsOf(allSteps, p);
    const restricted = ids.filter((id) => g.steps.includes(id));
    return restricted.length ? restricted : ids;
  });
};

const initialSteps = (group, allSteps) => (group?.pipelines || []).flatMap((p) => stepsOf(allSteps, p).filter((id) => (group.steps || []).includes(id)));
const wasWhole = (group, allSteps, p) => (group?.pipelines || []).includes(p) && !initialSteps({ ...group, pipelines: [p] }, allSteps).length;

function GroupDialog({ cfg, group, items, allSteps, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [picked, setPicked] = useState(group?.[cfg.field] || []);
  const [pickedSteps, setPickedSteps] = useState(() => (allSteps ? initialSteps(group, allSteps) : []));
  const [saving, setSaving] = useState(false);
  const toggle = (p) => {
    const on = picked.includes(p);
    setPicked(on ? picked.filter((x) => x !== p) : [...picked, p]);
    if (allSteps) { const ids = stepsOf(allSteps, p); if (on) setPickedSteps(pickedSteps.filter((x) => !ids.includes(x))); }
  };
  const toggleStep = (id) => setPickedSteps(pickedSteps.includes(id) ? pickedSteps.filter((x) => x !== id) : [...pickedSteps, id]);
  const save = () => {
    if (!name.trim() || picked.length === 0) { toast.warning(`Donnez un nom et cochez au moins un élément.`); return; }
    let steps = [];
    if (allSteps) {
      for (const p of picked) {
        const ids = stepsOf(allSteps, p);
        const chosen = ids.filter((id) => pickedSteps.includes(id));
        if (ids.length && !chosen.length) {
          if (wasWhole(group, allSteps, p)) continue;
          toast.warning(`Cochez au moins une étape pour « ${p} ».`); return;
        }
        if (chosen.length < ids.length) steps = steps.concat(chosen);
      }
    }
    setSaving(true);
    const body = { name, [cfg.field]: picked, hide_default: !!group?.hide_default, ...(allSteps ? { steps } : {}) };
    const req = group?.id ? api.put(`${cfg.endpoint}/${group.id}`, body) : api.post(cfg.endpoint, body);
    req.then(() => { toast.success(group?.id ? "Groupe modifié." : "Groupe créé."); onSaved(); onClose(); })
      .catch(() => toast.error("Impossible d'enregistrer le groupe."))
      .finally(() => setSaving(false));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${cfg.prefix}-dialog`}>
        <DialogHeader><DialogTitle>{group?.id ? "Modifier le groupe" : cfg.title}</DialogTitle></DialogHeader>
        <label className="agent-group-field">
          <span>Nom du groupe</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={cfg.placeholder} maxLength={60} data-testid={`${cfg.prefix}-name`} />
        </label>
        <div className="agent-group-field">
          <span>{cfg.noun[0].toUpperCase() + cfg.noun.slice(1)} ({picked.length})</span>
          <div className="agent-group-picklist">
            {items.map((it) => (
              <div key={it.id}>
                <label className="agent-filter-item">
                  <input type="checkbox" checked={picked.includes(it.id)} onChange={() => toggle(it.id)} data-testid={`${cfg.prefix}-opt-${it.id}`} />
                  <span>{it.name}</span>
                </label>
                {allSteps && picked.includes(it.id) && (
                  <div className="group-step-list" data-testid={`${cfg.prefix}-steps-${it.id}`}>
                    {allSteps.filter((o) => o.pipeline === it.id).map((o) => (
                      <label key={o.id} className="agent-filter-item">
                        <input type="checkbox" checked={pickedSteps.includes(o.id)} onChange={() => toggleStep(o.id)} data-testid={`${cfg.prefix}-step-${o.id}`} />
                        <span>{o.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <button type="button" className="secondary-button" onClick={onClose} data-testid={`${cfg.prefix}-cancel`}>Annuler</button>
          <button type="button" className="primary-button" disabled={saving} onClick={save} data-testid={`${cfg.prefix}-save`}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Groups({ cfg, groups, items, selected, onSelect, excluded, onExclude, onChanged, allSteps, steps = [], onSelectSteps, readOnly = false }) {
  const [editing, setEditing] = useState(null);
  const remove = (g) => {
    if (!window.confirm(`Supprimer le groupe « ${g.name} » ?`)) return;
    api.delete(`${cfg.endpoint}/${g.id}`).then(() => { toast.success("Groupe supprimé."); onChanged(); }).catch(() => toast.error("Suppression impossible."));
  };
  const ids = items.map((it) => it.id);
  return (
    <div className="agent-groups" data-testid={`${cfg.prefix}s`}>
      {groups.map((g) => {
        const avail = ids.filter((p) => g[cfg.field].includes(p));
        const exp = allSteps ? expandGroupSteps(g, allSteps) : [];
        const on = avail.length > 0 && avail.every((p) => selected.includes(p)) && exp.every((id) => steps.includes(id));
        const pipeSteps = allSteps ? avail.flatMap((p) => stepsOf(allSteps, p)) : [];
        const choose = () => {
          if (on) {
            onSelect(selected.filter((p) => !avail.includes(p)));
            if (onSelectSteps && steps.length) onSelectSteps(steps.filter((id) => !pipeSteps.includes(id)));
            return;
          }
          onSelect(Array.from(new Set([...selected, ...avail])));
          if (onSelectSteps && (exp.length || steps.length)) onSelectSteps(Array.from(new Set([...steps, ...(exp.length ? exp : pipeSteps)])));
        };
        const off = onExclude && avail.length > 0 && avail.every((p) => excluded.includes(p));
        return (
          <div key={g.id} className="agent-group-row">
            <button type="button" className={`agent-group-btn${on ? " agent-group-on" : ""}${off ? " agent-group-off" : ""}`} onClick={choose} data-testid={`${cfg.prefix}-select-${g.name}`}>
              {on ? `Retirer « ${g.name} »` : `${g.name} (${avail.length}${exp.length ? ` · ${exp.length} étapes` : ""})`}
            </button>
            {onExclude && <button type="button" className={`agent-group-icon${off ? " agent-group-icon-on" : ""}`} title={off ? "Réintégrer ce groupe" : "Exclure ce groupe du rapport"} onClick={() => onExclude(off ? excluded.filter((p) => !avail.includes(p)) : Array.from(new Set([...excluded, ...avail])))} data-testid={`${cfg.prefix}-exclude-${g.name}`}><Ban size={13} /></button>}
            {!readOnly && <button type="button" className="agent-group-icon" title="Modifier" onClick={() => setEditing(g)} data-testid={`${cfg.prefix}-edit-${g.name}`}><Pencil size={13} /></button>}
            {!readOnly && <button type="button" className="agent-group-icon" title="Supprimer" onClick={() => remove(g)} data-testid={`${cfg.prefix}-delete-${g.name}`}><Trash2 size={13} /></button>}
          </div>
        );
      })}
      {!readOnly && <button type="button" className="link-button agent-group-create" onClick={() => setEditing({})} data-testid={`${cfg.prefix}-create`}><Plus size={13} />Créer un groupe</button>}
      {editing && <GroupDialog cfg={cfg} group={editing} items={items} allSteps={allSteps} onClose={() => setEditing(null)} onSaved={onChanged} />}
    </div>
  );
}

export function PipelineGroups({ allPipelines, ...props }) {
  return <Groups cfg={PIPELINE_CFG} items={allPipelines.map((p) => ({ id: p, name: p }))} {...props} />;
}

export function AgentGroups(props) {
  return <Groups cfg={AGENT_CFG} {...props} allSteps={undefined} />;
}

export function TagGroups({ allTags, prefix, ...props }) {
  return <Groups cfg={prefix ? { ...TAG_CFG, prefix } : TAG_CFG} items={allTags.map((t) => ({ id: t, name: t }))} {...props} allSteps={undefined} />;
}
